import { useState } from "react";
import { useRedeemPoints, useRedeemablePreview } from "../hooks/usePoints";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { useNotification } from "@/shared/hooks/useNotification";

interface Props {
  customerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatKes = (n: number) =>
  new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES", minimumFractionDigits: 2 }).format(n);

/**
 * Redeem loyalty points to wallet cash. The redeemable preview enforces the
 * configured rules (min threshold, balance, optional cap, point value) before
 * the request is sent; the server re-validates.
 */
export const PointsRedeemDialog = ({ customerId, open, onOpenChange }: Props) => {
  const notification = useNotification();
  const mutation = useRedeemPoints();
  const [points, setPoints] = useState("");
  const [reference, setReference] = useState("");

  const preview = useRedeemablePreview(open ? customerId : undefined);

  const reset = () => {
    setPoints("");
    setReference("");
  };

  const submit = () => {
    const value = Number(points);
    if (!Number.isInteger(value) || value <= 0) {
      notification.error("Points must be a whole number greater than zero.");
      return;
    }

    if (p && value > p.maxRedeemablePoints) {
      notification.error(`Maximum redeemable now is ${p.maxRedeemablePoints} points.`);
      return;
    }

    mutation
      .mutateAsync({
        customerId,
        points: value,
        reference: reference.trim() || null,
      })
      .then(() => {
        notification.success("Redemption recorded. Pending finance approval.");
        reset();
        onOpenChange(false);
      })
      .catch((e) => {
        const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
        notification.error(msg ?? "Failed to redeem points.");
      });
  };

  const p = preview.data;
  const thresholdNotReached = !!p && p.pointsBalance < p.minRedeemablePoints;
  const noRedeemableNow = !!p && p.maxRedeemablePoints <= 0;
  const disableRedeemControls = mutation.isPending || preview.isError || thresholdNotReached || noRedeemableNow;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Redeem loyalty points</DialogTitle>
          <DialogDescription>
            Points are converted to wallet cash after finance approval.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {preview.isError ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
              Redemption is unavailable - no active approved points configuration.
            </div>
          ) : p ? (
            <div className="rounded-md border bg-muted/40 p-2 text-xs text-muted-foreground space-y-0.5">
              <p>
                Balance: <span className="font-medium text-foreground">{p.pointsBalance} points</span>
              </p>
              <p>Minimum redeemable: {p.minRedeemablePoints} points</p>
              <p>Point value: 1 point = {formatKes(p.pointValueKes)}</p>
              <p>
                Max redeemable now:{" "}
                <span className="font-medium text-foreground">
                  {p.maxRedeemablePoints} points ({formatKes(p.maxRedeemableKes)})
                </span>
              </p>
              {thresholdNotReached && (
                <p className="text-amber-700">
                  Redeeming is disabled until the minimum threshold is reached.
                  You need {p.minRedeemablePoints - p.pointsBalance} more points.
                </p>
              )}
            </div>
          ) : null}

          <Field>
            <FieldLabel>
              Points to redeem <span className="text-destructive">*</span>
            </FieldLabel>
            <Input
              type="number"
              step="1"
              min={1}
              max={p?.maxRedeemablePoints ?? undefined}
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              disabled={disableRedeemControls}
            />
          </Field>

          <Field>
            <FieldLabel>Reference</FieldLabel>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} disabled={disableRedeemControls} />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={disableRedeemControls}>
            {mutation.isPending ? "Redeeming..." : "Redeem points"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
